import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Content, colors } from "src/styles";
import { setPageNameHeader } from "src/redux/actions/header";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { RecurringScheduleItem, Ticket } from "src/types";

type RouteState = {
  ticket?: Ticket;
};

const readStringField = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = source[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const normalizeDetails = (input: unknown): Record<string, unknown> => {
  if (!input || typeof input !== "object") return {};

  const payload = input as Record<string, unknown>;
  const candidates: unknown[] = [
    payload,
    payload.data,
    (payload.data as Record<string, unknown> | undefined)?.data,
    payload.result,
    (payload.result as Record<string, unknown> | undefined)?.data,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;

    const record = candidate as Record<string, unknown>;

    if (Array.isArray(record.items)) {
      const fromItems = record.items.find((item) => {
        if (!item || typeof item !== "object") return false;
        const obj = item as Record<string, unknown>;
        const itemScheduleId = String(obj.scheduleId ?? obj.schedule_id ?? obj.id ?? "");
        return itemScheduleId.length > 0;
      });

      if (fromItems && typeof fromItems === "object") {
        return fromItems as Record<string, unknown>;
      }
    }

    const recordScheduleId = String(record.scheduleId ?? record.schedule_id ?? record.id ?? "");
    if (recordScheduleId.length > 0) {
      return record;
    }
  }

  return (payload.result as Record<string, unknown>) || payload;
};

const extractScheduledItems = (source: Record<string, unknown>): RecurringScheduleItem[] => {
  const raw = source.scheduledItems ?? source.scheduled_items ?? source.items;
  if (!Array.isArray(raw)) return [];

  return raw.filter((item): item is RecurringScheduleItem => !!item && typeof item === "object");
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const RecurringScheduleDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { scheduleId = "" } = useParams();
  const routeState = location.state as RouteState | null;
  const seedTicket = routeState?.ticket;
  const { id: loggedInUserId, isManager } = useSelector(getUserData);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [details, setDetails] = useState<Record<string, unknown>>({});
  const [scheduledItems, setScheduledItems] = useState<RecurringScheduleItem[]>(seedTicket?.scheduledItems ?? []);

  useEffect(() => {
    dispatch(setPageNameHeader("Recurring Schedule"));
    return () => {
      dispatch(setPageNameHeader("Work Requests"));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!scheduleId) return;

    const loadDetails = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await FirebaseDataService.getRecurringWorkOrderScheduleDetails(
          loggedInUserId,
          !!isManager,
          scheduleId,
        );
        const normalized = normalizeDetails(response);
        setDetails(normalized);
        const extractedItems = extractScheduledItems(normalized);
        setScheduledItems(extractedItems.length ? extractedItems : (seedTicket?.scheduledItems ?? []));
      } catch (requestError) {
        console.error("Failed to load recurring schedule details:", requestError);
        setError("Unable to load recurring schedule details.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadDetails();
  }, [loggedInUserId, isManager, scheduleId]);

  const summary = useMemo(() => {
    const title =
      readStringField(details, ["title", "name"]) ||
      seedTicket?.description ||
      readStringField(details, ["description"]);
    const recurrenceType = readStringField(details, ["recurrenceType", "recurrence_type", "frequency"]);
    const interval = readStringField(details, ["recurrenceInterval", "recurrence_interval"]);
    const status =
      readStringField(details, ["status", "statusDescription", "status_description"]) ||
      seedTicket?.status ||
      (details.isActive === undefined
        ? "-"
        : String(details.isActive).toLowerCase() === "true"
          ? "Active"
          : "Inactive");

    return {
      title: title || "Recurring Schedule",
      status,
      recurrence: recurrenceType ? `${recurrenceType}${interval ? ` (${interval})` : ""}` : "-",
      startDate: formatDateTime(readStringField(details, ["startDate", "start_date"])),
      endDate: formatDateTime(readStringField(details, ["endDate", "end_date"])),
      nextScheduledFor: formatDateTime(
        readStringField(details, ["nextScheduledFor", "next_scheduled_for", "nextRunDate"]),
      ),
      totalItems: readStringField(details, ["totalItems", "total_items"]) || String(scheduledItems.length),
    };
  }, [details, seedTicket, scheduledItems.length]);

  return (
    <Content>
      <HeaderRow>
        <BackButton type="button" onClick={() => navigate(-1)}>
          Back
        </BackButton>
        <div>
          <Eyebrow>Recurring Schedule</Eyebrow>
          <Title>{summary.title}</Title>
        </div>
      </HeaderRow>

      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Schedule ID</SummaryLabel>
          <SummaryValue>{scheduleId || "-"}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Status</SummaryLabel>
          <SummaryValue>{summary.status || "-"}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Recurrence</SummaryLabel>
          <SummaryValue>{summary.recurrence}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Start Date</SummaryLabel>
          <SummaryValue>{summary.startDate}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>End Date</SummaryLabel>
          <SummaryValue>{summary.endDate}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Next Scheduled</SummaryLabel>
          <SummaryValue>{summary.nextScheduledFor}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Total Scheduled Items</SummaryLabel>
          <SummaryValue>{summary.totalItems}</SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      <SectionTitle>Scheduled Items</SectionTitle>
      {isLoading ? <InfoText>Loading scheduled items...</InfoText> : null}
      {!isLoading && !!error ? <ErrorText>{error}</ErrorText> : null}
      {!isLoading && !scheduledItems.length ? <InfoText>No scheduled items were found.</InfoText> : null}

      {!isLoading && scheduledItems.length > 0 ? (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Item ID</th>
                <th>Location</th>
                <th>Scheduled For</th>
                <th>Status</th>
                <th>Assigned Resource</th>
                <th>Generated Work Order</th>
              </tr>
            </thead>
            <tbody>
              {scheduledItems.map((item) => {
                const assignedResource = item.assignedLaborCode || item.resourceId || item.resourceKind || "none";
                return (
                  <tr key={item.itemId || `${item.locationCode}-${item.scheduledFor}`}>
                    <td>{item.itemId || "-"}</td>
                    <td>{item.locationLabel || item.locationCode || "-"}</td>
                    <td>{formatDateTime(item.scheduledFor)}</td>
                    <td>{item.status || "-"}</td>
                    <td>{assignedResource}</td>
                    <td>
                      {item.generatedWonum ? (
                        <LinkButton type="button" onClick={() => navigate(`/workrequests/${item.generatedWonum}`)}>
                          {item.generatedWonum}
                        </LinkButton>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      ) : null}
    </Content>
  );
};

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
`;

const BackButton = styled.button`
  border: 1px solid ${colors.grey.default};
  background: ${colors.white.default};
  color: ${colors.black.dark1};
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: ${colors.grey.dark2};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Title = styled.h1`
  margin: 2px 0 0;
  color: ${colors.black.dark1};
  font-size: 22px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  border: 1px solid ${colors.grey.light3};
  background: ${colors.grey.light6};
  padding: 12px;
`;

const SummaryLabel = styled.div`
  color: ${colors.grey.dark3};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 5px;
`;

const SummaryValue = styled.div`
  color: ${colors.black.dark1};
  font-size: 13px;
  word-break: break-word;
`;

const SectionTitle = styled.h2`
  margin: 0 0 10px;
  color: ${colors.black.dark1};
  font-size: 18px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${colors.grey.light3};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th,
  td {
    border-bottom: 1px solid ${colors.grey.light3};
    padding: 10px 12px;
    text-align: left;
    font-size: 13px;
    color: ${colors.black.dark1};
  }

  th {
    background: ${colors.grey.light6};
    font-weight: 600;
  }
`;

const InfoText = styled.p`
  margin: 0;
  color: ${colors.grey.dark3};
  font-size: 13px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${colors.red.default};
  font-size: 13px;
`;

const LinkButton = styled.button`
  border: none;
  background: transparent;
  color: ${colors.blue.default};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
`;

export default RecurringScheduleDetails;
